import React from "react";
import { Box } from "@mui/material";
import { radius } from "../../config/theme";
import { useStatusColors } from "../../helpers/statusColors";

interface DeltaPillProps {
  /** The numeric value driving color/sign. null/NaN renders a neutral dash. */
  value: number | null | undefined;
  /** Pre-formatted display text. Defaults to the value with an explicit sign. */
  display?: React.ReactNode;
  /** Append a "%" to the default display. */
  suffix?: string;
  /**
   * Show a directional arrow (▲/▼) in addition to color, so +/- is
   * distinguishable without relying on color alone (accessibility).
   */
  showArrow?: boolean;
  /** Higher value = good (default). Set false for metrics where lower is better. */
  higherIsBetter?: boolean;
}

/**
 * Colored pill for signed metrics (CLV, ADP deltas). Green for positive,
 * red for negative, neutral for zero/missing. Single source for the
 * previously-duplicated `clvPillStyle` helpers across the app.
 */
export default function DeltaPill({
  value,
  display,
  suffix = "",
  showArrow = false,
  higherIsBetter = true,
}: DeltaPillProps) {
  const sc = useStatusColors();
  const num = typeof value === "number" && Number.isFinite(value) ? value : null;

  if (num === null) {
    return <Box component="span" sx={{ color: "text.secondary" }}>—</Box>;
  }

  const good = higherIsBetter ? num > 0 : num < 0;
  const bad = higherIsBetter ? num < 0 : num > 0;
  const color = good ? sc.good : bad ? sc.bad : "text.primary";
  const bg = good ? `${sc.good}18` : bad ? `${sc.bad}15` : "transparent";

  const defaultText = `${num > 0 ? "+" : ""}${num.toFixed(1)}${suffix}`;
  const arrow = num > 0 ? "▲ " : num < 0 ? "▼ " : "";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        bgcolor: bg,
        color,
        px: 0.75,
        py: 0.25,
        borderRadius: `${radius.sm}px`,
        fontWeight: 600,
        fontSize: 13,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {showArrow && arrow}
      {display ?? defaultText}
    </Box>
  );
}
